import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarPlus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { payBooking } from "@/lib/payments";
import { fmtINR, fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

export const ExtendModal = ({ booking, open, onClose, onDone }) => {
  const { user } = useAuth();
  const ranged = (booking?.items || []).filter((i) => i.unit === "day" || i.unit === "night");
  const currentEnd = ranged.length
    ? ranged.map((i) => i.end_date).sort().slice(-1)[0]
    : null;
  const [extendTo, setExtendTo] = useState("");
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setExtendTo("");
      setQuote(null);
    }
  }, [open]);

  const getQuote = async () => {
    if (!extendTo) return;
    setBusy(true);
    try {
      const r = await api.post(`/bookings/${booking.booking_id}/extend-quote`, { extend_to: extendTo });
      setQuote(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not calculate extension");
    } finally {
      setBusy(false);
    }
  };

  const pay = async () => {
    setBusy(true);
    try {
      const res = await payBooking({
        bookingId: booking.booking_id,
        kind: "extension",
        extendTo,
        user,
        onDemo: async (payment) => {
          toast.info(`Demo mode — Razorpay keys not configured yet. Simulating payment of ${fmtINR(payment.amount)}.`);
          return true;
        },
      });
      if (res.done) {
        toast.success("Stay extended and payment recorded!");
        onDone(res.booking);
        onClose();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Payment failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="extend-modal" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium text-ink">Extend the stay</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">
          Current check-out: <span className="font-semibold text-ink">{fmtDate(currentEnd)}</span>
        </p>
        <div className="mt-4">
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">New check-out date</label>
          <input
            type="date"
            data-testid="extend-date-input"
            value={extendTo}
            min={currentEnd}
            onChange={(e) => {
              setExtendTo(e.target.value);
              setQuote(null);
            }}
            className="mt-1.5 w-full rounded-2xl border border-ink/15 bg-cream px-4 py-3 text-sm text-ink focus:border-bark focus:outline-none focus:ring-2 focus:ring-bark/25"
          />
        </div>

        {quote && (
          <div data-testid="extend-quote" className="mt-5 space-y-2 rounded-2xl bg-cream-dark/60 p-5 text-sm">
            <div className="flex justify-between"><span className="text-slate-600">Extra {quote.extra_days} {quote.extra_days === 1 ? "day/night" : "days/nights"}</span><span className="font-semibold text-ink">{fmtINR(quote.extra_amount)}</span></div>
            <div className="flex justify-between"><span className="text-slate-600">50% of extension</span><span className="font-semibold text-ink">{fmtINR(Math.ceil(quote.extra_amount / 2))}</span></div>
            <div className="flex justify-between"><span className="text-slate-600">Previous balance</span><span className="font-semibold text-ink">{fmtINR(quote.previous_balance)}</span></div>
            <div className="flex justify-between border-t border-ink/10 pt-2"><span className="font-semibold text-navy">Due now</span><span className="font-display text-lg font-medium text-bark">{fmtINR(quote.due_now)}</span></div>
          </div>
        )}

        <div className="mt-6 flex gap-3">
          {!quote ? (
            <button
              type="button"
              data-testid="extend-quote-button"
              onClick={getQuote}
              disabled={!extendTo || busy}
              className="flex-1 rounded-full bg-navy px-6 py-3.5 text-sm font-semibold text-cream transition-colors duration-200 hover:bg-ink disabled:opacity-50"
            >
              {busy ? "Calculating…" : "Calculate amount"}
            </button>
          ) : (
            <button
              type="button"
              data-testid="extend-pay-button"
              onClick={pay}
              disabled={busy}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-bark px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-bark-dark disabled:opacity-50"
            >
              <CalendarPlus className="h-4 w-4" aria-hidden="true" />
              {busy ? "Processing…" : `Pay ${fmtINR(quote.due_now)} & extend`}
            </button>
          )}
        </div>
        <p className="mt-3 text-center text-xs text-slate-400">
          This reserves the extra days with 50% of the extension plus any pending balance. Boarding is
          billed on a 24-hour cycle from check-in; standard access is 8 AM–6 PM (early check-in / late
          pick-up charges apply outside these hours).
        </p>
      </DialogContent>
    </Dialog>
  );
};
